import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import {
  Button as AntdButton,
  ConfigProvider,
  Divider,
  Table,
  TableProps,
  Typography,
  message,
  theme as antdTheme,
  Input as AntdInput,
} from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import ProdutoModal, { valorNaUnidade } from "@/components/produtoModal";
import AlunoModal from "@/components/alunoModal";
import PagamentoParcelas from "@/components/PagamentoParcelas";
import { getAllProdutos } from "@/services/produto";
import { getVendaById, saveVenda } from "@/services/venda";
import { errorHandler, mostrarErro } from "@/utils/errorhandler";
import { normalizeDataParcelaToApi } from "@/utils/dateUtils";
import { getUnidadeLogadaId } from "@/utils/unidadeLogada";

const { Title } = Typography;

type ItemVenda = {
  produtoId: number;
  nome: string;
  valor: number;
};

type AlunoSelecionado = {
  id: number;
  nome: string;
  matricula?: number | string | null;
};

function formatCurrency(valor: number) {
  return (valor || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });
}

function parseCurrency(value: string) {
  if (!value) return 0;
  return Number(value.replace(/[^\d]/g, "")) / 100;
}

export default function CadastroVenda() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const unidadeId = useMemo(() => getUnidadeLogadaId(), []);

  // O bloco de pagamento é o mesmo da matrícula e trabalha sobre um formulário
  // em contexto (campos `pagamentos` e `cheques`).
  const methods = useForm<any>({
    defaultValues: { pagamentos: [], cheques: [] },
  });

  const [catalogo, setCatalogo] = useState<any[]>([]);
  const [aluno, setAluno] = useState<AlunoSelecionado | undefined>();
  const [observacao, setObservacao] = useState("");
  const [itens, setItens] = useState<ItemVenda[]>([]);
  const [modalProdutoAberto, setModalProdutoAberto] = useState(false);
  const [modalAlunoAberto, setModalAlunoAberto] = useState(false);

  useEffect(() => {
    const carregar = async () => {
      try {
        const produtosResponse = await getAllProdutos();
        setCatalogo((produtosResponse.data || []).filter((p: any) => p.ativo));

        if (isEdit && id) {
          const vendaResponse = await getVendaById(id);
          if (vendaResponse.status === 200 && vendaResponse.data) {
            const venda = vendaResponse.data;
            setAluno({
              id: venda.alunoId,
              nome: venda.alunoNome ?? "",
              matricula: venda.matricula,
            });
            setObservacao(venda.observacao ?? "");
            setItens(
              (venda.produtos || []).map((p: any) => ({
                produtoId: p.produtoId,
                nome: p.nome,
                valor: p.valor,
              }))
            );
            methods.setValue(
              "pagamentos",
              (venda.pagamentos || []).map((p: any) => ({
                pagamentoId: p.pagamentoId,
                parcela: p.parcela,
                tipoPagamento: p.tipoPagamento,
                nomePagamento: p.nomePagamento,
                dataParcela: p.dataParcela,
                valor: p.valor,
                cvCartao: p.cvCartao,
              }))
            );
            methods.setValue(
              "cheques",
              (venda.cheques || []).map((c: any) => ({
                id: c.id,
                banco: c.banco,
                agencia: c.agencia,
                conta: c.conta,
                numero: c.numero,
                nomeDonoCheque: c.nomeDonoCheque,
              }))
            );
          }
        }
      } catch (error) {
        errorHandler(error);
      }
    };
    carregar();
  }, [id, isEdit, methods]);

  /** Entram todos os produtos confirmados na modal de uma vez. */
  const adicionarProdutos = useCallback(
    (escolhidos: any[]) => {
      setItens((prev) => [
        ...prev,
        ...escolhidos.map((produto) => ({
          produtoId: produto.id,
          nome: produto.nome,
          valor: valorNaUnidade(produto, unidadeId),
        })),
      ]);
      setModalProdutoAberto(false);
    },
    [unidadeId]
  );

  const selecionarAluno = useCallback((registro: any) => {
    setAluno({ id: registro.id, nome: registro.nome, matricula: registro.matricula });
    setModalAlunoAberto(false);
  }, []);

  const total = itens.reduce((acc, item) => acc + (item.valor || 0), 0);

  const salvar = async () => {
    if (!aluno) {
      mostrarErro("Selecione o aluno da venda.");
      return;
    }
    if (itens.length === 0) {
      mostrarErro("Adicione ao menos um produto à venda.");
      return;
    }

    const pagamentos = methods.getValues("pagamentos") || [];
    if (pagamentos.length === 0) {
      mostrarErro("Adicione ao menos uma parcela de pagamento.");
      return;
    }

    const totalParcelas = pagamentos.reduce((acc: number, p: any) => acc + (p.valor || 0), 0);
    if (totalParcelas.toFixed(2) !== total.toFixed(2)) {
      mostrarErro("O total das parcelas deve ser igual ao total dos produtos da venda.");
      return;
    }

    const cheques = methods.getValues("cheques") || [];
    const parcelasCheque = pagamentos.filter((p: any) => p.tipoPagamento === "Cheque").length;
    if (parcelasCheque > cheques.length) {
      mostrarErro("Informe os dados dos cheques de todas as parcelas em cheque.");
      return;
    }

    try {
      const response = await saveVenda({
        id: id ? Number(id) : undefined,
        alunoId: aluno.id,
        observacao: observacao || null,
        produtos: itens.map((item) => ({ produtoId: item.produtoId, valor: item.valor })),
        pagamentos: pagamentos.map((pagamento: any) => ({
          pagamentoId: pagamento.pagamentoId,
          parcela: pagamento.parcela,
          dataParcela: normalizeDataParcelaToApi(pagamento.dataParcela) ?? pagamento.dataParcela,
          valor: pagamento.valor,
          cvCartao: pagamento.cvCartao ?? 0,
        })),
        cheques: cheques.map((cheque: any) => ({
          banco: cheque.banco,
          agencia: cheque.agencia,
          conta: cheque.conta,
          numero: Number(cheque.numero),
          nomeDonoCheque: cheque.nomeDonoCheque,
        })),
      });
      if (response.status === 200 || response.status === 201) {
        message.success("Venda registrada com sucesso!");
        navigate("/venda-produto");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const columns: TableProps<ItemVenda>["columns"] = [
    {
      title: "Produto",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Valor",
      key: "valor",
      width: 200,
      render: (_, item, index) => (
        <Input
          type="text"
          aria-label={`Valor - ${item.nome}`}
          value={formatCurrency(item.valor)}
          onChange={(e) => {
            const valor = parseCurrency(e.target.value);
            setItens((prev) => prev.map((i, idx) => (idx === index ? { ...i, valor } : i)));
          }}
        />
      ),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, __, index) => (
        <AntdButton
          type="text"
          danger
          icon={<DeleteOutlined />}
          title="Remover produto"
          onClick={() => setItens((prev) => prev.filter((_i, idx) => idx !== index))}
        />
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <div className="max-w-5xl mx-auto p-8">
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Venda" : "Nova Venda"}</span>
        </div>

        <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <label>Aluno *</label>
              <div className="flex gap-4 items-center">
                <AntdInput
                  readOnly
                  aria-label="Aluno"
                  placeholder="Nenhum aluno selecionado"
                  value={
                    aluno ? (aluno.matricula ? `${aluno.nome} (${aluno.matricula})` : aluno.nome) : ""
                  }
                />
                <AntdButton
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => setModalAlunoAberto(true)}
                >
                  Pesquisar aluno
                </AntdButton>
              </div>
            </div>

            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label>Produtos *</label>
                <AntdButton
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => setModalProdutoAberto(true)}
                >
                  Pesquisar produto
                </AntdButton>
              </div>
              <Table
                columns={columns}
                dataSource={itens.map((item, index) => ({ ...item, key: `${item.produtoId}-${index}` }))}
                rowKey="key"
                size="small"
                pagination={false}
                locale={{ emptyText: "Nenhum produto adicionado" }}
              />
              <div className="mt-3">
                <Title level={5}>{`Total da venda: ${formatCurrency(total)}`}</Title>
              </div>
            </div>

            <div className="col-span-2">
              <label>Observação</label>
              <AntdInput.TextArea
                rows={3}
                maxLength={4000}
                placeholder="Observação da venda"
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
              />
            </div>
          </div>

          <Divider />

          {/* Mesmo bloco de parcelas/cheques da matrícula de aluno. */}
          <PagamentoParcelas
            valorTotal={total}
            rotuloValorTotal="Valor Total da Venda"
            nomeParaCopiarCheque={aluno?.nome}
            rotuloCopiarCheque="Copiar Aluno"
          />

          <ProdutoModal
            open={modalProdutoAberto}
            produtos={catalogo}
            idsAdicionados={itens.map((i) => i.produtoId)}
            unidadeId={unidadeId}
            onConfirm={adicionarProdutos}
            onCancel={() => setModalProdutoAberto(false)}
          />

          <AlunoModal
            open={modalAlunoAberto}
            onSelect={selecionarAluno}
            onCancel={() => setModalAlunoAberto(false)}
          />
        </ConfigProvider>

        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/venda-produto")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="button" onClick={salvar}>
            Salvar
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
